import React, { useState, useEffect } from "react";
import { NavbarDemo } from "./NavBar";
import {
  Cookie,
  Settings,
  CheckCircle,
  XCircle,
  Info,
  Shield,
} from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";

const ManageCookies = () => {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
  });

  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    // Load saved preferences from localStorage
    const saved = localStorage.getItem("cookiePreferences");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCookiePreferences({
          necessary: true, // Always required
          analytics: parsed.analytics || false,
          marketing: parsed.marketing || false,
          functional: parsed.functional || false,
        });
      } catch (e) {
        console.error("Error loading cookie preferences:", e);
      }
    }
  }, []);

  const handleToggle = (category) => {
    if (category === "necessary") return; // Cannot disable necessary cookies

    setCookiePreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    // Save preferences to localStorage
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences),
    );

    // Update cookie consent banner if it exists
    if (window.updateCookiePreferences) {
      window.updateCookiePreferences(cookiePreferences);
    }

    setSaveStatus("success");
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem("cookiePreferences", JSON.stringify(allAccepted));

    if (window.updateCookiePreferences) {
      window.updateCookiePreferences(allAccepted);
    }

    setSaveStatus("success");
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem("cookiePreferences", JSON.stringify(onlyNecessary));

    if (window.updateCookiePreferences) {
      window.updateCookiePreferences(onlyNecessary);
    }

    setSaveStatus("success");
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const cookieTypes = [
    {
      id: "necessary",
      name: "Necessary Cookies",
      description:
        "These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies.",
      examples: [
        "Session management",
        "Authentication",
        "Security tokens",
        "CSRF protection",
      ],
      required: true,
    },
    {
      id: "analytics",
      name: "Analytics Cookies",
      description:
        "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and services.",
      examples: [
        "Page views",
        "User behavior tracking",
        "Performance metrics",
        "Error tracking",
      ],
      required: false,
    },
    {
      id: "functional",
      name: "Functional Cookies",
      description:
        "These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings. They may be set by us or by third-party providers.",
      examples: [
        "Language preferences",
        "Region settings",
        "Remembered login information",
        "User preferences",
      ],
      required: false,
    },
    {
      id: "marketing",
      name: "Marketing Cookies",
      description:
        "These cookies are used to track visitors across websites to display relevant advertisements. They may also be used to measure the effectiveness of advertising campaigns.",
      examples: [
        "Advertising cookies",
        "Social media integration",
        "Retargeting pixels",
        "Conversion tracking",
      ],
      required: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <NavbarDemo />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
            <Cookie className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Manage Cookie Preferences
          </h1>
          <p className="text-gray-600">
            Control how we use cookies and similar technologies on our website
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-900 font-medium mb-1">About Cookies</p>
              <p className="text-blue-800 text-sm leading-relaxed">
                Cookies are small text files that are placed on your device when
                you visit our website. They help us provide you with a better
                experience by remembering your preferences and understanding how
                you use our site. You can choose which types of cookies to
                accept below.
              </p>
            </div>
          </div>
        </div>

        {/* Cookie Types */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          {cookieTypes.map((type) => (
            <div
              key={type.id}
              className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {type.name}
                    </h3>
                    {type.required && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    {type.description}
                  </p>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Examples:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-2">
                      {type.examples.map((example, idx) => (
                        <li key={idx}>{example}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {type.required ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm">Always On</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Label
                        htmlFor={type.id}
                        className="text-sm text-gray-700 cursor-pointer">
                        {cookiePreferences[type.id] ? "Enabled" : "Disabled"}
                      </Label>
                      <Switch
                        id={type.id}
                        checked={cookiePreferences[type.id]}
                        onCheckedChange={() => handleToggle(type.id)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Quick Actions
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleAcceptAll}
              className="bg-green-600 hover:bg-green-700 text-white">
              Accept All Cookies
            </Button>
            <Button
              onClick={handleRejectAll}
              variant="outline"
              className="border-gray-300">
              Reject All Optional
            </Button>
            <Button
              onClick={handleSave}
              className="bg-purple-600 hover:bg-purple-700 text-white">
              Save Preferences
            </Button>
          </div>
        </div>

        {/* Save Status */}
        {saveStatus === "success" && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-green-800">
              Your cookie preferences have been saved successfully!
            </p>
          </div>
        )}

        {/* Additional Information */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-8 space-y-6">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-purple-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Privacy and Data Protection
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to protecting your privacy. The information
              collected through cookies is used in accordance with our{" "}
              <a
                href="/privacy-policy"
                className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
              . We use industry-standard security measures to protect your data.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You can change your cookie preferences at any time by returning to
              this page. Note that disabling certain cookies may affect the
              functionality of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Third-Party Cookies
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some cookies on our website are set by third-party services. These
              include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                <strong>Google Analytics:</strong> Used to analyze website
                traffic and user behavior
              </li>
              <li>
                <strong>Payment Processors:</strong> Secure payment processing
                and fraud prevention
              </li>
              <li>
                <strong>Social Media Platforms:</strong> Integration with social
                media features
              </li>
              <li>
                <strong>Advertising Networks:</strong> Targeted advertising and
                marketing campaigns
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              These third parties have their own privacy policies and cookie
              practices. We recommend reviewing their policies for more
              information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Managing Cookies in Your Browser
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can also manage cookies directly through your browser
              settings. Here are links to instructions for popular browsers:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline">
                  Google Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline">
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline">
                  Safari
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline">
                  Microsoft Edge
                </a>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Please note that blocking all cookies may impact your ability to
              use certain features of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Do Not Track
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Some browsers include a "Do Not Track" (DNT) feature that signals
              websites you visit that you do not want to have your online
              activity tracked. Currently, there is no standard for how DNT
              signals should be interpreted. Our website does not currently
              respond to DNT signals, but we respect your cookie preferences as
              set on this page.
            </p>
          </section>

          <section className="bg-purple-50 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Questions About Cookies?
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about our use of cookies or this Cookie
              Management page, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-blue-600 hover:underline">
                  info@bookatherapy.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                <a
                  href="tel:+1-780-267-7551"
                  className="text-blue-600 hover:underline">
                  +1 (780) 267-7551
                </a>
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageCookies;
